import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ApiError, uuid } from "@gjwtrack/client";
import type { AdminBundleElement, AdminOverview, AdminProject } from "@gjwtrack/domain";
import type { FieldDef, FieldKind, FieldValueSet, ProjectFieldRef } from "@gjwtrack/query-language";
import { ChevronRight, GripVertical, Pencil, Plus, Trash2, TriangleAlert, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { client, useClient } from "../../lib/client.ts";
import { Button, Checkbox, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel, Select, SelectType, type SelectItem } from "../../ui/ring.ts";
import { SimpleSelect } from "../../ui/SimpleSelect.tsx";
import { ValueSelect } from "../../ui/ValueSelect.tsx";
import { SettingsTitle } from "./ProjectSettings.tsx";
import { type Run } from "./shared.tsx";

const BUNDLE_KINDS: ReadonlySet<string> = new Set(["enum", "state", "version", "build", "owned"]);
const KINDS: FieldKind[] = ["enum", "state", "user", "group", "version", "build", "owned", "date", "datetime", "period", "integer", "float", "string", "text"];
const COLORS = [0, 1, 2, 3, 4, 5, 6, 7, 8];

/**
 * YouTrack's project Custom Fields page: the attached fields on the left, the
 * selected field's settings on the right. Everything saves on change.
 *
 * Not implemented: "Replace field" and "Make private" from the toolbar, and
 * "Merge with" in the Values list.
 */
export function ProjectFields({ p, data, run, reload }: { p: AdminProject; data: AdminOverview; run: Run; reload: () => Promise<void> }) {
  const schema = useClient((s) => s.schema);
  const [selected, setSelected] = useState<number | null>(null);
  const [details, setDetails] = useState(true);
  const [adding, setAdding] = useState(false);
  const [aliasFor, setAliasFor] = useState<number | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  if (!schema) return null;

  const attached = (schema.projectFieldsByProject.get(p.id) ?? [])
    .map((pf) => ({ pf, def: schema.fieldsById.get(pf.fieldId) }))
    .filter((x): x is { pf: ProjectFieldRef; def: FieldDef } => !!x.def);
  const current = attached.find((a) => a.def.id === selected) ?? null;

  const onDragEnd = (e: DragEndEvent): void => {
    const ids = attached.map((a) => a.def.id);
    const from = ids.indexOf(Number(e.active.id));
    const to = e.over ? ids.indexOf(Number(e.over.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    void run("reorderProjectFields", { projectId: p.id, fieldIds: arrayMove(ids, from, to) });
  };

  return (
    <div>
      <SettingsTitle title="Custom Fields" />
      <div className="mb-3 flex items-center gap-2">
        <Button icon={Plus} onClick={() => setAdding(true)}>
          Add field to project...
        </Button>
        <Button icon={Pencil} title="Change this field's name in this project (its alias)" disabled={!current} onClick={() => current && setAliasFor(current.def.id)} />
        <Button
          icon={Trash2}
          title="Remove the field from this project"
          disabled={!current}
          onClick={() => {
            if (!current) return;
            if (confirm(`Remove ${current.pf.alias ?? current.def.name} from ${p.key}? Values already stored on the issues stay.`)) {
              void run("detachField", { projectId: p.id, fieldId: current.def.id }).then((ok) => ok && setSelected(null));
            }
          }}
        />
        <div className="flex-1" />
        <Button onClick={() => setDetails((d) => !d)}>{details ? "Hide details" : "Show details"}</Button>
      </div>

      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-[1.5rem_1.25rem_minmax(0,1fr)_8rem_11rem] items-center gap-2 border-b border-line pb-1 text-xs font-medium text-muted uppercase">
            <span />
            <span />
            <span>Name</span>
            <span>Type</span>
            <span>Default Value(s)</span>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={attached.map((a) => a.def.id)} strategy={verticalListSortingStrategy}>
              {attached.map(({ pf, def }) => (
                <FieldRow
                  key={def.id}
                  pf={pf}
                  def={def}
                  data={data}
                  selected={selected === def.id}
                  onSelect={() => setSelected((s) => (s === def.id ? null : def.id))}
                  onDefault={(ids) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, defaultValue: ids.length ? ids : null })}
                />
              ))}
            </SortableContext>
          </DndContext>
          {attached.length === 0 && <div className="py-3 text-faint">No fields in this project yet.</div>}
        </div>

        {details && (
          <div className="w-[27rem] shrink-0 rounded border border-line bg-surface px-4 py-3">
            {current ? (
              <FieldDetails key={current.def.id} p={p} data={data} pf={current.pf} def={current.def} run={run} reload={reload} />
            ) : (
              <div className="py-6 text-center text-faint">Select a field to see its settings.</div>
            )}
          </div>
        )}
      </div>

      {adding && <AddFieldDialog p={p} data={data} run={run} reload={reload} onClose={() => setAdding(false)} />}
      {aliasFor !== null && current && (
        <TextDialog
          title={`${current.def.name}: alias in ${p.key}`}
          label="Alias"
          hint="A project-local name for the field. The query language accepts both."
          value={current.pf.alias ?? ""}
          onClose={() => setAliasFor(null)}
          onSave={(v) => void run("updateProjectField", { projectId: p.id, fieldId: current.def.id, alias: v.trim() || null }).then((ok) => ok && setAliasFor(null))}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------- the left table

function FieldRow({ pf, def, data, selected, onSelect, onDefault }: { pf: ProjectFieldRef; def: FieldDef; data: AdminOverview; selected: boolean; onSelect: () => void; onDefault: (ids: number[]) => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: def.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`grid grid-cols-[1.5rem_1.25rem_minmax(0,1fr)_8rem_11rem] items-center gap-2 border-b border-line/60 py-1 ${isDragging ? "bg-surface shadow" : ""} ${selected ? "bg-sunken" : ""}`}
    >
      <Checkbox checked={selected} onChange={onSelect} />
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} type="button" title="Drag to reorder" tabIndex={-1} className="flex h-6 w-5 cursor-grab items-center justify-center text-faint active:cursor-grabbing">
        <GripVertical size={14} />
      </button>
      <button type="button" className="truncate text-left" onClick={onSelect}>
        {pf.alias ?? def.name}
        {pf.alias && <span className="ml-2 text-xs text-faint">{def.name}</span>}
      </button>
      <span className="truncate text-muted">
        {def.kind} ({def.multi ? "multi" : "single"})
      </span>
      <DefaultValue pf={pf} def={def} data={data} onChange={onDefault} />
    </div>
  );
}

/** Default Value(s): the option set's values for a bundle field; a warning when the field is required and has none. */
function DefaultValue({ pf, def, data, onChange }: { pf: ProjectFieldRef; def: FieldDef; data: AdminOverview; onChange: (ids: number[]) => void }) {
  const bundle = def.bundleId === undefined ? undefined : data.bundles.find((b) => b.id === def.bundleId);
  const values = (pf.defaultValue ?? []).filter((v): v is number => typeof v === "number");
  const missing = pf.required && values.length === 0;
  if (!bundle) {
    // A typed field's default is not editable here (YouTrack does not offer one either); show what is stored.
    return missing ? (
      <span className="flex items-center gap-1 text-xs text-warn" title="This field is required but has no default: every new issue has to set it by hand.">
        <TriangleAlert size={13} /> No value (required)
      </span>
    ) : (
      <span className="truncate text-faint">{pf.defaultValue?.join(", ") || "—"}</span>
    );
  }
  const empty = pf.required ? "No value (required)" : "No default";
  return (
    <span className={`flex min-w-0 items-center gap-1 ${missing ? "text-warn" : ""}`}>
      {missing && <TriangleAlert size={13} className="shrink-0" />}
      <ValueSelect
        inline
        multi={def.multi}
        clearable
        clearLabel={empty}
        emptyLabel={empty}
        options={bundle.elements
          .filter((e) => !e.archived)
          .map((e) => ({ id: e.id, label: e.name, icon: <span className={`h-3 w-3 rounded-sm ${e.colorId ? `chip-${((e.colorId - 1) % 8) + 1}` : ""}`} style={{ background: "var(--chip-fg, var(--faint))" }} /> }))}
        selected={new Set(values)}
        onChange={onChange}
      />
    </span>
  );
}

// ----------------------------------------------------------------- the details panel

/** The details panel is narrow, so its rows use a tighter label column than the settings pages' `Row`. */
function DetailRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-start gap-3 border-b border-line/60 py-1.5 last:border-0">
      <div className="pt-1.5 text-sm text-muted">{label}</div>
      <div className="min-w-0">
        {children}
        {hint && <div className="mt-0.5 text-xs text-faint">{hint}</div>}
      </div>
    </div>
  );
}

function FieldDetails({ p, data, pf, def, run, reload }: { p: AdminProject; data: AdminOverview; pf: ProjectFieldRef; def: FieldDef; run: Run; reload: () => Promise<void> }) {
  const [editing, setEditing] = useState<null | "name" | "empty" | "alias">(null);
  const [advanced, setAdvanced] = useState(false);
  const [valueSet, setValueSet] = useState(false);
  const bundle = def.bundleId === undefined ? undefined : data.bundles.find((b) => b.id === def.bundleId);
  const emptyDefault = `No ${(pf.alias ?? def.name).toLowerCase()}`;

  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="truncate text-[15px] font-semibold">{pf.alias ?? def.name}</h2>
        <Button icon={Pencil} title="Rename the field everywhere" onClick={() => setEditing("name")} />
      </div>

      <DetailRow label="Default Value">
        <DefaultValue pf={pf} def={def} data={data} onChange={(ids) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, defaultValue: ids.length ? ids : null })} />
      </DetailRow>

      <DetailRow label="Empty Value" hint="Shown on the issue when this field has no value.">
        <span className="flex items-center gap-1">
          <span className={`truncate ${pf.emptyText ? "" : "text-faint"}`}>{pf.emptyText ?? emptyDefault}</span>
          <Button icon={Pencil} title="Change the empty value text" onClick={() => setEditing("empty")} />
        </span>
      </DetailRow>

      <DetailRow label="Can be empty">
        <SimpleSelect
          full
          value={pf.required ? "no" : "yes"}
          options={[
            { value: "yes", label: "Can be empty" },
            { value: "no", label: "Cannot be empty" },
          ]}
          onChange={(v) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, required: v === "no" })}
        />
      </DetailRow>

      <DetailRow label="Type">
        <div className="flex flex-col items-stretch gap-1.5">
          <span className="py-1">{def.kind}</span>
          <MultiPicker def={def} reload={reload} />
        </div>
      </DetailRow>

      <DetailRow label="Aliases">
        <span className="flex items-center gap-1">
          <span className={`truncate ${pf.alias ? "" : "text-faint"}`}>{pf.alias ?? "none"}</span>
          <Button icon={Pencil} title="Change the alias in this project" onClick={() => setEditing("alias")} />
        </span>
      </DetailRow>

      <button type="button" className="mt-2 flex items-center gap-1 text-left text-sm text-muted hover:text-ink" onClick={() => setAdvanced((a) => !a)}>
        <ChevronRight size={14} className={`transition-transform ${advanced ? "rotate-90" : ""}`} /> Advanced Settings
      </button>
      {advanced && (
        <div className="mt-1 border-t border-line/60 pt-1">
          {def.kind === "user" ? (
            <DetailRow label="Values" hint="Who can be a value of this field in this project.">
              <button type="button" className="py-1.5 text-left text-value hover:underline" onClick={() => setValueSet(true)}>
                {valueSetSummary(pf.valueSet ?? null, pf.userIds ?? null)}
              </button>
            </DetailRow>
          ) : (
            <p className="py-2 text-xs text-faint">Nothing to configure for a {def.kind} field.</p>
          )}
        </div>
      )}

      {bundle && <Values bundle={bundle} data={data} run={run} reload={reload} />}

      {editing === "name" && (
        <TextDialog
          title={`Rename ${def.name}`}
          label="Name"
          hint="The field's name in every project. Use the alias below to rename it here only."
          value={def.name}
          onClose={() => setEditing(null)}
          onSave={(v) => v.trim() && void run("updateCustomField", { fieldId: def.id, name: v.trim() }).then((ok) => ok && setEditing(null))}
        />
      )}
      {editing === "empty" && (
        <TextDialog
          title={`${pf.alias ?? def.name}: empty value`}
          label="Empty value"
          hint={`Leave blank for the default, "${emptyDefault}".`}
          value={pf.emptyText ?? ""}
          onClose={() => setEditing(null)}
          onSave={(v) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, emptyText: v.trim() || null }).then((ok) => ok && setEditing(null))}
        />
      )}
      {editing === "alias" && (
        <TextDialog
          title={`${def.name}: alias in ${p.key}`}
          label="Alias"
          hint="A project-local name for the field. The query language accepts both."
          value={pf.alias ?? ""}
          onClose={() => setEditing(null)}
          onSave={(v) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, alias: v.trim() || null }).then((ok) => ok && setEditing(null))}
        />
      )}
      {valueSet && (
        <ValueSetDialog
          data={data}
          name={pf.alias ?? def.name}
          set={pf.valueSet ?? null}
          onClose={() => setValueSet(false)}
          onSave={(set) => void run("updateProjectField", { projectId: p.id, fieldId: def.id, valueSet: set }).then((ok) => ok && setValueSet(false))}
        />
      )}
    </div>
  );
}

/** "Can specify multiple values" / "Can specify a single value". The server refuses the second while an issue holds two. */
function MultiPicker({ def, reload }: { def: FieldDef; reload: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  const change = async (multi: boolean): Promise<void> => {
    setError(null);
    try {
      await client.http.command("updateCustomField", { fieldId: def.id, multi }, uuid());
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };
  return (
    <>
      <SimpleSelect
        full
        value={def.multi ? "multi" : "single"}
        options={[
          { value: "multi", label: "Can specify multiple values" },
          { value: "single", label: "Can specify a single value" },
        ]}
        onChange={(v) => void change(v === "multi")}
      />
      {error && <span className="text-xs text-danger">{error}</span>}
    </>
  );
}

// ----------------------------------------------------------------- Values (option sets)

function Values({ bundle, data, run, reload }: { bundle: AdminOverview["bundles"][number]; data: AdminOverview; run: Run; reload: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [error, setError] = useState<{ elementId: number; message: string } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const others = data.bundles.filter((b) => b.kind === bundle.kind && b.id !== bundle.id);

  const onDragEnd = (e: DragEndEvent): void => {
    const ids = bundle.elements.map((x) => x.id);
    const from = ids.indexOf(Number(e.active.id));
    const to = e.over ? ids.indexOf(Number(e.over.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    void run("reorderBundleElements", { bundleId: bundle.id, elementIds: arrayMove(ids, from, to) });
  };
  const remove = async (el: AdminBundleElement): Promise<void> => {
    setError(null);
    try {
      await client.http.command("deleteBundleElement", { elementId: el.id }, uuid());
      await reload();
    } catch (e) {
      setError({ elementId: el.id, message: e instanceof ApiError ? e.message : (e as Error).message });
    }
  };

  return (
    <div className="mt-4 border-t border-line pt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <b className="text-[13px]">{bundle.elements.length} Values</b>
        <div className="flex-1" />
        <Button icon={Plus} onClick={() => setAdding(true)}>
          Add new value
        </Button>
      </div>
      <div className="mb-2 grid grid-cols-2 gap-2">
        <SimpleSelect
          full
          value="manual"
          options={[
            { value: "manual", label: "Sort manually" },
            { value: "alpha", label: "Sort alphabetically" },
          ]}
          onChange={(v) => {
            if (v !== "alpha") return;
            const ids = [...bundle.elements].sort((a, b) => a.name.localeCompare(b.name)).map((e) => e.id);
            void run("reorderBundleElements", { bundleId: bundle.id, elementIds: ids });
          }}
        />
        <SimpleSelect
          full
          value=""
          placeholder="Copy values from"
          disabled={others.length === 0}
          options={others.map((b) => ({ value: String(b.id), label: b.name }))}
          onChange={(v) => v && void run("copyBundleValues", { bundleId: bundle.id, fromBundleId: Number(v) })}
        />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={bundle.elements.map((e) => e.id)} strategy={verticalListSortingStrategy}>
          {bundle.elements.map((el) => (
            <ValueRow
              key={el.id}
              el={el}
              stateful={bundle.kind === "state"}
              editing={editing === el.id}
              error={error?.elementId === el.id ? error.message : null}
              onEdit={() => setEditing((e) => (e === el.id ? null : el.id))}
              onSave={(patch) => void run("updateBundleElement", { elementId: el.id, ...patch }).then((ok) => ok && setEditing(null))}
              onDelete={() => void remove(el)}
              onArchive={() => void run("updateBundleElement", { elementId: el.id, archived: true }).then((ok) => ok && setError(null))}
            />
          ))}
        </SortableContext>
      </DndContext>
      {adding && (
        <TextDialog
          title={`${bundle.name}: new value`}
          label="Name"
          value=""
          onClose={() => setAdding(false)}
          onSave={(v) => v.trim() && void run("addBundleElement", { bundleId: bundle.id, name: v.trim(), isResolved: false, colorId: null }).then((ok) => ok && setAdding(false))}
        />
      )}
    </div>
  );
}

function ValueRow({
  el,
  stateful,
  editing,
  error,
  onEdit,
  onSave,
  onDelete,
  onArchive,
}: {
  el: AdminBundleElement;
  stateful: boolean;
  editing: boolean;
  error: string | null;
  onEdit: () => void;
  onSave: (patch: Record<string, unknown>) => void;
  onDelete: () => void;
  onArchive: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: el.id });
  const [name, setName] = useState(el.name);
  useEffect(() => setName(el.name), [el.name]);
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} className={`relative border-b border-line/60 py-1 ${isDragging ? "bg-surface shadow" : ""}`}>
      <div className="flex items-center gap-2">
        <button ref={setActivatorNodeRef} {...attributes} {...listeners} type="button" title="Drag to reorder" tabIndex={-1} className="flex h-6 w-5 cursor-grab items-center justify-center text-faint active:cursor-grabbing">
          <GripVertical size={14} />
        </button>
        <span className={`chip ${el.colorId ? `chip-${((el.colorId - 1) % 8) + 1}` : ""} ${el.archived ? "opacity-50" : ""}`}>{el.name}</span>
        {el.archived && <span className="text-xs text-faint">archived</span>}
        {stateful && el.isResolved && <span className="text-xs text-muted">resolved</span>}
        <div className="flex-1" />
        <Button icon={Pencil} title="Edit this value" onClick={onEdit} />
        <Button icon={X} title="Delete this value" onClick={onDelete} />
      </div>
      {error && (
        <div className="mt-1 flex items-center gap-2 rounded bg-danger-soft px-2 py-1 text-xs text-danger">
          <span className="flex-1">{error}</span>
          <Button inline onClick={onArchive}>
            Archive instead
          </Button>
        </div>
      )}
      {editing && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded border border-line bg-surface p-3 shadow-lg">
          <Input label="Name" size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} />
          <div className="mt-2 mb-1 text-xs text-muted">Colour</div>
          <div className="flex flex-wrap items-center gap-1">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`h-5 w-5 rounded ${c > 0 ? `chip-${c}` : ""} ${(el.colorId ?? 0) === c ? "ring-2 ring-focus" : ""}`}
                style={{ background: "var(--chip-bg, var(--sunken))" }}
                title={c === 0 ? "No colour" : `Colour ${c}`}
                onClick={() => onSave({ colorId: c === 0 ? null : c })}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-col gap-1">
            {stateful && <Checkbox label="Resolved" checked={el.isResolved} onChange={(e) => onSave({ isResolved: e.target.checked })} />}
            <Checkbox label="Archived" checked={el.archived} onChange={(e) => onSave({ archived: e.target.checked })} />
          </div>
          <div className="mt-3 flex gap-2">
            <Button primary disabled={!name.trim() || name === el.name} onClick={() => onSave({ name: name.trim() })}>
              Save
            </Button>
            <Button onClick={onEdit}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- dialogs

/** One-line editor used for the field name, the alias, the empty value and a new option value. */
export function TextDialog({ title, label, hint, value, onClose, onSave }: { title: string; label: string; hint?: string; value: string; onClose: () => void; onSave: (value: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <Dialog show label={title} onCloseAttempt={onClose} showCloseButton trapFocus contentClassName="!w-[28rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>{title}</DialogHeader>
      <DialogContent>
        <Input label={label} size={InputSize.FULL} value={v} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSave(v)} />
        {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
      </DialogContent>
      <Panel>
        <Button primary onClick={() => onSave(v)}>
          Save
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}

/** "Add field to project": an existing field, or a new one created and attached in one go. */
function AddFieldDialog({ p, data, run, reload, onClose }: { p: AdminProject; data: AdminOverview; run: Run; reload: () => Promise<void>; onClose: () => void }) {
  const schema = useClient((s) => s.schema);
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [fieldId, setFieldId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<FieldKind>("enum");
  const [multi, setMulti] = useState(false);
  const [bundleId, setBundleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!schema) return null;

  const attached = new Set((schema.projectFieldsByProject.get(p.id) ?? []).map((pf) => pf.fieldId));
  const available = schema.data.fields.filter((f) => !attached.has(f.id));
  const needsBundle = BUNDLE_KINDS.has(kind);
  const bundlesFor = data.bundles.filter((b) => b.kind === kind);

  const submit = async (): Promise<void> => {
    setError(null);
    if (mode === "existing") {
      if (!fieldId) return;
      if (await run("attachField", { projectId: p.id, fieldId: Number(fieldId), required: false, alias: null })) onClose();
      return;
    }
    setBusy(true);
    try {
      let bid: number | null = null;
      if (needsBundle) {
        bid = bundleId && bundleId !== "new" ? Number(bundleId) : (await client.http.command<{ id: number }>("createBundle", { name: `${name.trim()} values`, kind }, uuid())).id;
      }
      const f = await client.http.command<{ id: number }>("createCustomField", { name: name.trim(), kind, multi, bundleId: bid }, uuid());
      await client.http.command("attachField", { projectId: p.id, fieldId: f.id, required: false, alias: null }, uuid());
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog show label="Add field to project" onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false} contentClassName="!w-[34rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>Add field to project</DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2">
            <input type="radio" name="add-field-mode" checked={mode === "existing"} onChange={() => setMode("existing")} />
            Existing field
          </label>
          <div className={mode === "existing" ? "pl-6" : "pl-6 pointer-events-none opacity-50"}>
            <SimpleSelect full value={fieldId} placeholder={available.length ? "Select a field…" : "Every field is already here"} options={available.map((f) => ({ value: String(f.id), label: `${f.name} (${f.kind})` }))} onChange={setFieldId} />
          </div>
          <label className="flex items-center gap-2">
            <input type="radio" name="add-field-mode" checked={mode === "new"} onChange={() => setMode("new")} />
            Create new field
          </label>
          <div className={`flex flex-wrap items-end gap-2 pl-6 ${mode === "new" ? "" : "pointer-events-none opacity-50"}`}>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <label className="block">
              <span className="mb-1 block text-xs text-muted">Type</span>
              <SimpleSelect value={kind} options={KINDS.map((k) => ({ value: k, label: k }))} onChange={(v) => setKind(v as FieldKind)} />
            </label>
            <Checkbox label="multiple values" containerClassName="pb-1.5" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
            {needsBundle && (
              <label className="block">
                <span className="mb-1 block text-xs text-muted">Option set</span>
                <SimpleSelect value={bundleId || "new"} options={[{ value: "new", label: "New option set…" }, ...bundlesFor.map((b) => ({ value: String(b.id), label: b.name }))]} onChange={setBundleId} />
              </label>
            )}
          </div>
          {error && <div className="rounded bg-danger-soft px-2 py-1 text-danger">{error}</div>}
          <p className="text-xs text-faint">
            Fields and option sets live on <Link to="/admin/fields" className="text-value hover:underline">Custom Fields</Link>; attaching one here does not change the other projects that use it.
          </p>
        </div>
      </DialogContent>
      <Panel>
        <Button primary loader={busy} disabled={busy || (mode === "existing" ? !fieldId : !name.trim())} onClick={() => void submit()}>
          Add
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}

// ----------------------------------------------------------------- user field value sets

export function valueSetSummary(set: FieldValueSet | null, userIds: number[] | null): string {
  if (!set) return "Anyone";
  const parts: string[] = [];
  if (set.users.length) parts.push(`${set.users.length} user${set.users.length === 1 ? "" : "s"}`);
  if (set.groups.length) parts.push(`${set.groups.length} group${set.groups.length === 1 ? "" : "s"}`);
  if (set.projectTeam) parts.push("project team");
  return `${parts.join(", ") || "nobody"} (${userIds?.length ?? 0} people)`;
}

/** YouTrack's "Values" of a user field: pick individuals, groups, and/or the project team; nothing = anyone. */
export function ValueSetDialog({ data, name, set, onClose, onSave }: { data: AdminOverview; name: string; set: FieldValueSet | null; onClose: () => void; onSave: (set: FieldValueSet | null) => void }) {
  const [users, setUsers] = useState<number[]>(set?.users ?? []);
  const [groups, setGroups] = useState<number[]>(set?.groups ?? []);
  const [projectTeam, setProjectTeam] = useState(set?.projectTeam ?? false);
  const [restricted, setRestricted] = useState(set !== null);
  const userItems: SelectItem[] = data.users.filter((u) => u.status !== "disabled").map((u) => ({ key: u.id, label: u.name, description: u.email }));
  const groupItems: SelectItem[] = data.groups.map((g) => ({ key: g.id, label: g.name }));
  return (
    <Dialog show label={`${name}: values`} onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false} contentClassName="!w-[36rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>{name}: who can be a value</DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-3">
          <Checkbox label="Anyone (no restriction)" checked={!restricted} onChange={(e) => setRestricted(!e.target.checked)} />
          <div className={restricted ? "" : "pointer-events-none opacity-50"}>
            <div className="mb-1 text-xs text-muted">Users</div>
            <Select multiple filter type={SelectType.BUTTON} label="Add users…" data={userItems} selected={userItems.filter((i) => users.includes(Number(i.key)))} onChange={(items: SelectItem[]) => setUsers(items.map((i) => Number(i.key)))} />
            <div className="mt-3 mb-1 text-xs text-muted">Groups</div>
            <Select multiple filter type={SelectType.BUTTON} label="Add groups…" data={groupItems} selected={groupItems.filter((i) => groups.includes(Number(i.key)))} onChange={(items: SelectItem[]) => setGroups(items.map((i) => Number(i.key)))} />
            <div className="mt-3">
              <Checkbox label="Project team (everyone with a role in this project)" checked={projectTeam} onChange={(e) => setProjectTeam(e.target.checked)} />
            </div>
          </div>
        </div>
      </DialogContent>
      <Panel>
        <Button primary onClick={() => onSave(restricted ? { users, groups, projectTeam } : null)}>
          Save
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
